import React from "react";
import { FileImage, Search } from "lucide-react";

import LocationPickerMap from "@/components/LocationPickerMap";
import {
  DIRECTORY_FOCUS_OPTIONS,
  DIRECTORY_TYPE_OPTIONS,
  INITIATIVE_ACCESS_OPTIONS,
  PLATFORM_LINKS,
  POPULATION_FOCUS_OPTIONS,
} from "@/lib/polenica";

import { inputClass, joinLocation, textareaClass } from "./config";
import {
  ChoiceGrid,
  Field,
  MultiSelectGrid,
  StatusChip,
  UploadCard,
} from "./primitives";

function renderSelectOptions(options) {
  return options.map((option) => {
    if (typeof option === "string") {
      return (
        <option key={option} value={option}>
          {option}
        </option>
      );
    }

    return (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    );
  });
}

export function VisibilityToggles({ value, onChange, includeHours = false, includePopulationFocus = false }) {
  const options = [
    ["publicar_nombre_contacto", "Nombre de contacto"],
    ["publicar_celular_contacto", "Teléfono"],
    ["publicar_email_contacto", "Correo"],
    ["publicar_direccion", "Dirección exacta"],
    ...(includeHours ? [["publicar_horarios", "Horarios"]] : []),
    ["publicar_whatsapp", "Botón de WhatsApp"],
    ...(includePopulationFocus
      ? [["publicar_enfoque_poblacional", "Enfoques poblacionales en filtros públicos"]]
      : []),
  ];

  return (
    <div className="rounded-[22px] border border-primary/12 bg-white px-4 py-4">
      <p className="text-sm font-semibold text-primary">Datos autorizados para vista pública</p>
      <p className="mt-1 text-xs leading-5 text-mutedForeground">
        La plataforma conserva la información interna; solo se mostrará públicamente lo que marque aquí.
      </p>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {options.map(([key, label]) => (
          <label key={key} className="flex items-center gap-3 rounded-2xl border border-border bg-[#F9FBF5] px-3 py-2">
            <input
              type="checkbox"
              checked={Boolean(value[key])}
              onChange={(event) => onChange(key, event.target.checked)}
              className="h-4 w-4 rounded border-primary text-primary"
            />
            <span className="text-sm text-textMain">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export function PersonalStep({
  personal,
  personalErrors,
  stepHasAttempt,
  setPersonal,
}) {
  const updatePersonal = (field, value) => {
    setPersonal((current) => ({ ...current, [field]: value }));
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Nombres" required error={stepHasAttempt ? personalErrors.nombres : ""}>
        <input
          value={personal.nombres}
          onChange={(event) => updatePersonal("nombres", event.target.value)}
          className={inputClass(stepHasAttempt && Boolean(personalErrors.nombres))}
          placeholder="Ingrese sus nombres"
        />
      </Field>

      <Field label="Apellidos" required error={stepHasAttempt ? personalErrors.apellidos : ""}>
        <input
          value={personal.apellidos}
          onChange={(event) => updatePersonal("apellidos", event.target.value)}
          className={inputClass(stepHasAttempt && Boolean(personalErrors.apellidos))}
          placeholder="Ingrese sus apellidos"
        />
      </Field>

      <Field label="Correo" required error={stepHasAttempt ? personalErrors.email : ""}>
        <input
          type="email"
          value={personal.email}
          onChange={(event) => updatePersonal("email", event.target.value)}
          className={inputClass(stepHasAttempt && Boolean(personalErrors.email))}
          placeholder="correo@ejemplo.com"
        />
      </Field>

      <Field
        label="Contraseña"
        required
        error={stepHasAttempt ? personalErrors.password : ""}
        hint="Debe incluir mínimo 8 caracteres, una mayúscula y un número."
      >
        <input
          type="password"
          value={personal.password}
          onChange={(event) => updatePersonal("password", event.target.value)}
          className={inputClass(stepHasAttempt && Boolean(personalErrors.password))}
          placeholder="********"
        />
      </Field>

    </div>
  );
}

export function InitiativeStep({
  wizard,
  initiative,
  initiativeErrors,
  stepHasAttempt,
  publishedInitiatives,
  searchQuery,
  setSearchQuery,
  selectedExistingInitiative,
  setSelectedExistingInitiative,
  initiativeFile,
  initiativeUpload,
  initiativePreviewUrl,
  setWizard,
  setInitiative,
  onInitiativeFileChange,
  mapOptions,
  municipalities,
  getComunasForMunicipio,
  getBarriosForComuna,
}) {
  const usesMedellin = initiative.municipio === "Medellin";
  const comunas = getComunasForMunicipio(initiative.municipio);
  const barrios = getBarriosForComuna(initiative.municipio, initiative.comuna);
  const hasMunicipalityBarrios = !usesMedellin && barrios.length > 0;

  const filteredInitiatives = publishedInitiatives.filter((item) => {
    if (!searchQuery.trim()) {
      return true;
    }

    const haystack = [
      item.nombre,
      item.municipio,
      item.comuna,
      item.barrio,
      item.direccion,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(searchQuery.trim().toLowerCase());
  });

  const updateInitiative = (field, value) => {
    setInitiative((current) => ({ ...current, [field]: value }));
  };

  return (
    <div className="space-y-5">
      <ChoiceGrid
        title="¿Actualmente hace parte de una huerta o paca urbana?"
        description="Este paso es opcional. Puede vincular una iniciativa existente, registrar una nueva o continuar sin mapa."
        value={wizard.mapParticipation}
        onChange={(value) => {
          setWizard((current) => ({
            ...current,
            mapParticipation: value,
            iniciativa_existente_id: value === "existing" ? current.iniciativa_existente_id : null,
          }));
          if (value !== "existing") {
            setSelectedExistingInitiative(null);
          }
        }}
        options={mapOptions}
      />

      {stepHasAttempt && initiativeErrors.mapParticipation ? (
        <p className="text-sm font-medium text-[#8A3D22]">{initiativeErrors.mapParticipation}</p>
      ) : null}

      {wizard.mapParticipation === "existing" ? (
        <div className="rounded-[24px] border border-primary/10 bg-[#F9FBF5] p-5">
          <Field label="Buscar iniciativa registrada" required error={stepHasAttempt ? initiativeErrors.iniciativa_existente_id : ""}>
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-white px-4">
              <Search className="h-4 w-4 text-primary/60" />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="h-12 w-full bg-transparent outline-none"
                placeholder="Nombre, barrio o dirección"
              />
            </div>
          </Field>

          <div className="mt-4 space-y-3">
            {filteredInitiatives.slice(0, 8).map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setWizard((current) => ({ ...current, iniciativa_existente_id: item.id }));
                  setSelectedExistingInitiative(item);
                }}
                className={`w-full rounded-[20px] border p-4 text-left transition ${
                  wizard.iniciativa_existente_id === item.id
                    ? "border-primary bg-primary/5"
                    : "border-border bg-white hover:border-primary/30"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <StatusChip tone="positive">Iniciativa visible</StatusChip>
                  <StatusChip>{item.tipo}</StatusChip>
                </div>
                <p className="mt-3 font-semibold text-primary">{item.nombre}</p>
                <p className="mt-1 text-sm text-mutedForeground">
                  {joinLocation([item.municipio, item.comuna, item.barrio])}
                </p>
                <p className="mt-1 text-sm text-textMain">{item.direccion || "Sin dirección registrada"}</p>
              </button>
            ))}
          </div>

          {selectedExistingInitiative ? (
            <div className="mt-4 rounded-[20px] border border-[#5FA36A]/25 bg-[#EFF8EF] p-4">
              <div className="flex flex-wrap items-center gap-2">
                <StatusChip tone="positive">Seleccionada</StatusChip>
                <StatusChip>{selectedExistingInitiative.tipo}</StatusChip>
              </div>
              <p className="mt-3 font-heading text-xl font-semibold text-primary">{selectedExistingInitiative.nombre}</p>
              <p className="mt-2 text-sm text-textMain">
                {joinLocation([
                  selectedExistingInitiative.municipio,
                  selectedExistingInitiative.comuna,
                  selectedExistingInitiative.barrio,
                ])}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {wizard.mapParticipation === "new" ? (
        <div className="rounded-[24px] border border-primary/10 bg-[#F9FBF5] p-5">
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <StatusChip tone="warning">Nueva iniciativa</StatusChip>
            <StatusChip>{initiative.tipo_iniciativa}</StatusChip>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Nombre de la iniciativa" required error={stepHasAttempt ? initiativeErrors.nombre : ""}>
              <input
                value={initiative.nombre}
                onChange={(event) => updateInitiative("nombre", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(initiativeErrors.nombre))}
              />
            </Field>

            <Field label="Tipo de iniciativa" required>
              <select
                value={initiative.tipo_iniciativa}
                onChange={(event) => updateInitiative("tipo_iniciativa", event.target.value)}
                className={inputClass(false)}
              >
                <option value="HUERTA">Huerta</option>
                <option value="PACA_DIGESTORA">Paca urbana</option>
                <option value="HUERTA_PACA_URBANA">Huerta y paca urbana</option>
              </select>
            </Field>

            <Field label="Tipo de acceso" required>
              <select
                value={initiative.tipo_acceso}
                onChange={(event) => updateInitiative("tipo_acceso", event.target.value)}
                className={inputClass(false)}
              >
                {INITIATIVE_ACCESS_OPTIONS.filter((option) => option.value !== "PRIVADO").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Dirección" required error={stepHasAttempt ? initiativeErrors.direccion : ""}>
              <input
                value={initiative.direccion}
                onChange={(event) => updateInitiative("direccion", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(initiativeErrors.direccion))}
              />
            </Field>

            <Field label="País" required>
              <input value="Colombia" disabled className={inputClass(false)} />
            </Field>

            <Field label="Región / departamento" required>
              <input value="Antioquia" disabled className={inputClass(false)} />
            </Field>

            <Field label="Municipio" required error={stepHasAttempt ? initiativeErrors.municipio : ""}>
              <select
                value={initiative.municipio}
                onChange={(event) => {
                  const nextMunicipio = event.target.value;
                  const nextBarrios = getBarriosForComuna(nextMunicipio, "");
                  setInitiative((current) => ({
                    ...current,
                    ciudad: nextMunicipio,
                    municipio: nextMunicipio,
                    comuna: nextMunicipio === "Medellin" ? "" : "No aplica",
                    barrio: nextMunicipio === "Medellin" || nextBarrios.length ? "" : "No aplica",
                  }));
                }}
                className={inputClass(stepHasAttempt && Boolean(initiativeErrors.municipio))}
              >
                {renderSelectOptions(municipalities)}
              </select>
            </Field>

            <Field label="Comuna o corregimiento" required error={stepHasAttempt ? initiativeErrors.comuna : ""}>
              <select
                value={initiative.comuna}
                onChange={(event) =>
                  setInitiative((current) => ({
                    ...current,
                    comuna: event.target.value,
                    barrio: "",
                  }))
                }
                className={inputClass(stepHasAttempt && Boolean(initiativeErrors.comuna))}
                disabled={!usesMedellin}
              >
                <option value="">{usesMedellin ? "Seleccione una comuna" : "No aplica"}</option>
                {usesMedellin ? renderSelectOptions(comunas) : null}
              </select>
            </Field>

            <Field label="Barrio" required error={stepHasAttempt ? initiativeErrors.barrio : ""}>
              <select
                value={initiative.barrio}
                onChange={(event) => updateInitiative("barrio", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(initiativeErrors.barrio))}
                disabled={!usesMedellin && !hasMunicipalityBarrios}
              >
                <option value="">{usesMedellin || hasMunicipalityBarrios ? "Seleccione un barrio" : "No aplica"}</option>
                {usesMedellin || hasMunicipalityBarrios ? renderSelectOptions(barrios) : null}
              </select>
            </Field>

            <Field label="Nombre de contacto" required error={stepHasAttempt ? initiativeErrors.nombre_contacto : ""}>
              <input
                value={initiative.nombre_contacto}
                onChange={(event) => updateInitiative("nombre_contacto", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(initiativeErrors.nombre_contacto))}
              />
            </Field>

            <Field label="Celular de contacto" required error={stepHasAttempt ? initiativeErrors.celular_contacto : ""}>
              <input
                value={initiative.celular_contacto}
                onChange={(event) => updateInitiative("celular_contacto", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(initiativeErrors.celular_contacto))}
              />
            </Field>

            <Field label="Correo de contacto" required error={stepHasAttempt ? initiativeErrors.email_contacto : ""}>
              <input
                type="email"
                value={initiative.email_contacto}
                onChange={(event) => updateInitiative("email_contacto", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(initiativeErrors.email_contacto))}
              />
            </Field>

            <Field label="Año de inicio" error={stepHasAttempt ? initiativeErrors.ano_inicio : ""}>
              <input
                type="number"
                value={initiative.ano_inicio}
                onChange={(event) => updateInitiative("ano_inicio", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(initiativeErrors.ano_inicio))}
                placeholder="2024"
              />
            </Field>

            <Field label="Sitio web">
              <input
                value={initiative.sitio_web}
                onChange={(event) => updateInitiative("sitio_web", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <Field label="Instagram">
              <input
                value={initiative.instagram_url}
                onChange={(event) => updateInitiative("instagram_url", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <Field label="Facebook">
              <input
                value={initiative.facebook_url}
                onChange={(event) => updateInitiative("facebook_url", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <Field label="WhatsApp">
              <input
                value={initiative.whatsapp_url}
                onChange={(event) => updateInitiative("whatsapp_url", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <div className="md:col-span-2">
              <VisibilityToggles
                value={initiative}
                onChange={updateInitiative}
                includeHours
                includePopulationFocus
              />
            </div>

            <div className="md:col-span-2">
              <Field label="Descripción" required error={stepHasAttempt ? initiativeErrors.descripcion : ""}>
                <textarea
                  rows={4}
                  value={initiative.descripcion}
                  onChange={(event) => updateInitiative("descripcion", event.target.value)}
                  className={textareaClass(stepHasAttempt && Boolean(initiativeErrors.descripcion))}
                />
              </Field>
            </div>

            <div className="md:col-span-2">
              <Field
                label="Enfoque poblacional"
                hint="Puede elegir más de una opción."
                error={stepHasAttempt ? initiativeErrors.enfoque_poblacional : ""}
              >
                <MultiSelectGrid
                  options={POPULATION_FOCUS_OPTIONS}
                  value={initiative.enfoque_poblacional}
                  onToggle={(item) =>
                    setInitiative((current) => ({
                      ...current,
                      enfoque_poblacional: current.enfoque_poblacional.includes(item)
                        ? current.enfoque_poblacional.filter((currentItem) => currentItem !== item)
                        : [...current.enfoque_poblacional, item],
                    }))
                  }
                />
              </Field>
            </div>

            <div className="md:col-span-2">
              <Field
                label="Ubicación aproximada en el mapa"
                hint={`Latitud ${initiative.latitud.toFixed(6)} / Longitud ${initiative.longitud.toFixed(6)}`}
              >
                <LocationPickerMap
                  value={{ lat: initiative.latitud, lng: initiative.longitud }}
                  searchContext={{
                    direccion: initiative.direccion,
                    barrio: initiative.barrio,
                    comuna: initiative.comuna,
                    municipio: initiative.municipio,
                  }}
                  onChange={(coordinate) =>
                    setInitiative((current) => ({
                      ...current,
                      latitud: coordinate.lat,
                      longitud: coordinate.lng,
                    }))
                  }
                />
              </Field>
            </div>

            <div className="md:col-span-2">
              <UploadCard
                title={initiativeFile ? initiativeFile.name : initiativeUpload ? "Imagen principal cargada" : "Cargar imagen principal"}
                subtitle="Agregue una imagen de referencia para identificar la iniciativa."
                icon={FileImage}
                onChange={onInitiativeFileChange}
              />
              {stepHasAttempt && initiativeErrors.image ? (
                <p className="mt-2 text-xs font-medium text-[#8A3D22]">{initiativeErrors.image}</p>
              ) : null}
              {initiativeUpload?.secure_url || initiativePreviewUrl ? (
                <div className="mt-3 overflow-hidden rounded-[20px] border border-border bg-white">
                  <img
                    src={initiativeUpload?.secure_url || initiativePreviewUrl}
                    alt="Vista previa iniciativa"
                    className="h-48 w-full object-cover"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function PublicationStep({
  wizard,
  publication,
  publicationErrors,
  stepHasAttempt,
  publishedInitiatives,
  publicationFiles,
  publicationUploads,
  publicationPreviewUrls,
  setWizard,
  setPublication,
  onPublicationFilesChange,
  municipalities,
  getComunasForMunicipio,
  getBarriosForComuna,
  directoryOptions,
}) {
  const usesMedellin = publication.municipio === "Medellin";
  const comunas = getComunasForMunicipio(publication.municipio);
  const barrios = getBarriosForComuna(publication.municipio, publication.comuna);
  const hasMunicipalityBarrios = !usesMedellin && barrios.length > 0;
  const locationNotApplicable = publication.municipio === "No aplica";

  const updatePublication = (field, value) => {
    setPublication((current) => ({ ...current, [field]: value }));
  };

  return (
    <div className="space-y-5">
      <ChoiceGrid
        title="¿Tiene un producto o servicio relacionado con huertas y agroecología?"
        description="Compártalo en el directorio y ayude a que el polen de la economía local siga circulando."
        value={wizard.directoryChoice}
        onChange={(value) => setWizard((current) => ({ ...current, directoryChoice: value }))}
        options={directoryOptions}
        columnsClass="md:grid-cols-2"
      />

      <p className="text-sm text-mutedForeground">
        Antes de participar en el directorio, consulte la{" "}
        <a href={PLATFORM_LINKS.directoryConditions} target="_blank" rel="noreferrer" className="font-semibold text-primary underline decoration-primary/30 underline-offset-4">
          información general y condiciones del directorio
        </a>
        .
      </p>

      {wizard.directoryChoice === "share" ? (
        <div className="rounded-[24px] border border-primary/10 bg-[#F9FBF5] p-5">
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <StatusChip tone="warning">Oferta inicial</StatusChip>
            <StatusChip>{DIRECTORY_TYPE_OPTIONS.find((item) => item.value === publication.tipo_oferta)?.label}</StatusChip>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Título" required error={stepHasAttempt ? publicationErrors.titulo : ""}>
              <input
                value={publication.titulo}
                onChange={(event) => updatePublication("titulo", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(publicationErrors.titulo))}
              />
            </Field>

            <Field label="Tipo" required>
              <select
                value={publication.tipo_oferta}
                onChange={(event) => updatePublication("tipo_oferta", event.target.value)}
                className={inputClass(false)}
              >
                {DIRECTORY_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Municipio" required error={stepHasAttempt ? publicationErrors.municipio : ""}>
              <select
                value={publication.municipio}
                onChange={(event) => {
                  const nextMunicipio = event.target.value;
                  const nextBarrios = getBarriosForComuna(nextMunicipio, "");
                  setPublication((current) => ({
                    ...current,
                    municipio: nextMunicipio,
                    comuna: nextMunicipio === "Medellin" ? "" : "No aplica",
                    barrio: nextMunicipio === "Medellin" || nextBarrios.length ? "" : "No aplica",
                    direccion: nextMunicipio === "No aplica" ? "No aplica" : current.direccion,
                    publicar_direccion: nextMunicipio === "No aplica" ? false : current.publicar_direccion,
                  }));
                }}
                className={inputClass(stepHasAttempt && Boolean(publicationErrors.municipio))}
              >
                <option value="No aplica">No aplica</option>
                {renderSelectOptions(municipalities)}
              </select>
            </Field>

            <Field label="Comuna o corregimiento" required error={stepHasAttempt ? publicationErrors.comuna : ""}>
              <select
                value={publication.comuna}
                onChange={(event) =>
                  setPublication((current) => ({
                    ...current,
                    comuna: event.target.value,
                    barrio: "",
                  }))
                }
                className={inputClass(stepHasAttempt && Boolean(publicationErrors.comuna))}
                disabled={!usesMedellin}
              >
                <option value="">{usesMedellin ? "Seleccione una comuna" : "No aplica"}</option>
                {usesMedellin ? <option value="No aplica">No aplica</option> : null}
                {usesMedellin ? renderSelectOptions(comunas) : null}
              </select>
            </Field>

            <Field label="Barrio" hint="Opcional si su producto o servicio no tiene una sede física.">
              <select
                value={publication.barrio}
                onChange={(event) => updatePublication("barrio", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(publicationErrors.barrio))}
                disabled={!usesMedellin && !hasMunicipalityBarrios}
              >
                <option value="">{usesMedellin || hasMunicipalityBarrios ? "Seleccione un barrio" : "No aplica"}</option>
                {usesMedellin || hasMunicipalityBarrios ? <option value="No aplica">No aplica</option> : null}
                {usesMedellin || hasMunicipalityBarrios ? renderSelectOptions(barrios) : null}
              </select>
            </Field>

            <Field label="Dirección" hint="Opcional si no atiende en un lugar físico.">
              <div className="flex gap-2">
                <input
                  value={publication.direccion}
                  onChange={(event) => updatePublication("direccion", event.target.value)}
                  className={inputClass(false)}
                  disabled={locationNotApplicable}
                />
                <button
                  type="button"
                  onClick={() =>
                    setPublication((current) => ({
                      ...current,
                      direccion: current.direccion === "No aplica" ? "" : "No aplica",
                      publicar_direccion: false,
                    }))
                  }
                  className="shrink-0 rounded-2xl border border-primary/15 bg-white px-3 text-xs font-semibold text-primary"
                >
                  {publication.direccion === "No aplica" ? "Ingresar dirección" : "No aplica"}
                </button>
              </div>
            </Field>

            <Field label="Nombre de contacto" required error={stepHasAttempt ? publicationErrors.nombre_contacto : ""}>
              <input
                value={publication.nombre_contacto}
                onChange={(event) => updatePublication("nombre_contacto", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(publicationErrors.nombre_contacto))}
              />
            </Field>

            <Field label="Celular de contacto" required error={stepHasAttempt ? publicationErrors.celular_contacto : ""}>
              <input
                value={publication.celular_contacto}
                onChange={(event) => updatePublication("celular_contacto", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(publicationErrors.celular_contacto))}
              />
            </Field>

            <Field label="Correo de contacto" required error={stepHasAttempt ? publicationErrors.email_contacto : ""}>
              <input
                type="email"
                value={publication.email_contacto}
                onChange={(event) => updatePublication("email_contacto", event.target.value)}
                className={inputClass(stepHasAttempt && Boolean(publicationErrors.email_contacto))}
              />
            </Field>

            <Field label="Sitio web">
              <input
                value={publication.sitio_web}
                onChange={(event) => updatePublication("sitio_web", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <Field label="Instagram">
              <input
                value={publication.instagram_url}
                onChange={(event) => updatePublication("instagram_url", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <Field label="Facebook">
              <input
                value={publication.facebook_url}
                onChange={(event) => updatePublication("facebook_url", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <Field label="WhatsApp">
              <input
                value={publication.whatsapp_url}
                onChange={(event) => updatePublication("whatsapp_url", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <div className="md:col-span-2">
              <VisibilityToggles value={publication} onChange={updatePublication} />
            </div>

            <div className="md:col-span-2">
              <Field label="Descripción breve" required error={stepHasAttempt ? publicationErrors.descripcion : ""}>
                <textarea
                  rows={4}
                  value={publication.descripcion}
                  onChange={(event) => updatePublication("descripcion", event.target.value)}
                  className={textareaClass(stepHasAttempt && Boolean(publicationErrors.descripcion))}
                />
              </Field>
            </div>

            <div className="md:col-span-2">
              <Field label="Enfoques asociados" hint="Puede elegir más de una opción.">
                <MultiSelectGrid
                  options={DIRECTORY_FOCUS_OPTIONS}
                  value={publication.enfoques_asociados}
                  onToggle={(item) =>
                    setPublication((current) => ({
                      ...current,
                      enfoques_asociados: current.enfoques_asociados.includes(item)
                        ? current.enfoques_asociados.filter((currentItem) => currentItem !== item)
                        : [...current.enfoques_asociados, item],
                    }))
                  }
                />
              </Field>
            </div>

            {!locationNotApplicable && publication.direccion !== "No aplica" ? (
              <div className="md:col-span-2">
                <Field
                  label="Ubicación aproximada en el mapa"
                  hint={`Latitud ${publication.latitud.toFixed(6)} / Longitud ${publication.longitud.toFixed(6)}`}
                >
                  <LocationPickerMap
                    value={{ lat: publication.latitud, lng: publication.longitud }}
                    searchContext={{
                      direccion: publication.direccion,
                      barrio: publication.barrio,
                      comuna: publication.comuna,
                      municipio: publication.municipio,
                    }}
                    onChange={(coordinate) =>
                      setPublication((current) => ({
                        ...current,
                        latitud: coordinate.lat,
                        longitud: coordinate.lng,
                      }))
                    }
                  />
                </Field>
              </div>
            ) : null}

            <div className="md:col-span-2">
              <UploadCard
                title={
                  publicationFiles.length
                    ? `${publicationFiles.length} imagen(es) seleccionada(s)`
                    : publicationUploads.length
                      ? `${publicationUploads.length} imagen(es) lista(s)`
                      : "Cargar imagen o imágenes"
                }
                subtitle="Agregue hasta 5 imágenes de referencia para la oferta."
                icon={FileImage}
                multiple
                onChange={onPublicationFilesChange}
              />
              {stepHasAttempt && publicationErrors.imagenes ? (
                <p className="mt-2 text-xs font-medium text-[#8A3D22]">{publicationErrors.imagenes}</p>
              ) : null}

              {publicationPreviewUrls.length ? (
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                  {publicationPreviewUrls.map((url, index) => (
                    <div key={`${url}-${index}`} className="overflow-hidden rounded-[18px] border border-border bg-white">
                      <img src={url} alt={`Vista previa ${index + 1}`} className="h-28 w-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="rounded-[22px] border border-primary/12 bg-white px-4 py-4 md:col-span-2">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={publication.acepta_deslinde_responsabilidad}
                  onChange={(event) => updatePublication("acepta_deslinde_responsabilidad", event.target.checked)}
                  className="mt-1 h-5 w-5 shrink-0 rounded border-primary text-primary"
                />
                <span className="text-sm leading-6 text-textMain">
                  Acepto el deslinde de responsabilidad sobre la información compartida en el directorio.
                </span>
              </label>
              {stepHasAttempt && publicationErrors.acepta_deslinde_responsabilidad ? (
                <p className="mt-3 text-xs font-medium text-[#8A3D22]">
                  {publicationErrors.acepta_deslinde_responsabilidad}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ConfirmationStep({
  personal,
  wizard,
  selectedExistingInitiative,
  initiative,
  publication,
  publicationPreviewUrls,
  finalAcknowledged,
  confirmationErrors,
  stepHasAttempt,
  setFinalAcknowledged,
  setPersonal,
  onEditStep,
}) {
  return (
    <div className="space-y-5">
      <div className="rounded-[24px] border border-primary/12 bg-[#F9FBF5] p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary/70">Datos personales</p>
        <p className="mt-2 text-lg font-semibold text-primary">
          {personal.nombres} {personal.apellidos}
        </p>
        <p className="mt-1 text-sm text-mutedForeground">{personal.email}</p>
        <button type="button" onClick={() => onEditStep(1)} className="mt-3 text-sm font-semibold text-primary underline underline-offset-4">
          Modificar datos personales
        </button>
      </div>

      <div className="rounded-[24px] border border-primary/12 bg-[#F9FBF5] p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary/70">Mapa</p>
        {wizard.mapParticipation === "existing" && selectedExistingInitiative ? (
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap gap-2">
              <StatusChip tone="positive">Iniciativa vinculada</StatusChip>
              <StatusChip>{selectedExistingInitiative.tipo}</StatusChip>
            </div>
            <p className="font-semibold text-primary">{selectedExistingInitiative.nombre}</p>
          </div>
        ) : null}
        {wizard.mapParticipation === "new" ? (
          <div className="mt-3 space-y-2 text-sm text-textMain">
            <div className="flex flex-wrap gap-2">
              <StatusChip tone="warning">Nueva iniciativa</StatusChip>
              <StatusChip>{initiative.tipo_iniciativa}</StatusChip>
            </div>
            <p className="font-semibold text-primary">{initiative.nombre}</p>
            <p>{initiative.descripcion}</p>
            <p>{joinLocation([initiative.municipio, initiative.comuna, initiative.barrio])}</p>
          </div>
        ) : null}
        {wizard.mapParticipation === "none" ? (
          <p className="mt-2 text-sm text-textMain">Continuará sin vincular una iniciativa en este registro.</p>
        ) : null}
        <button type="button" onClick={() => onEditStep(2)} className="mt-3 text-sm font-semibold text-primary underline underline-offset-4">
          Modificar información del mapa
        </button>
      </div>

      <div className="rounded-[24px] border border-primary/12 bg-[#F9FBF5] p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary/70">Directorio</p>
        {wizard.directoryChoice === "share" ? (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap gap-2">
              <StatusChip tone="warning">Oferta inicial</StatusChip>
              <StatusChip>{DIRECTORY_TYPE_OPTIONS.find((item) => item.value === publication.tipo_oferta)?.label}</StatusChip>
            </div>
            <div>
              <p className="font-semibold text-primary">{publication.titulo}</p>
              <p className="mt-1 text-sm text-textMain">{publication.descripcion}</p>
            </div>
            {publicationPreviewUrls.length ? (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {publicationPreviewUrls.map((url, index) => (
                  <div key={`${url}-${index}`} className="overflow-hidden rounded-2xl border border-border bg-white">
                    <img src={url} alt={`Vista previa ${index + 1}`} className="h-24 w-full object-cover" />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <p className="mt-2 text-sm text-textMain">No se generará una publicación inicial en este paso.</p>
        )}
        <button type="button" onClick={() => onEditStep(3)} className="mt-3 text-sm font-semibold text-primary underline underline-offset-4">
          Modificar información del directorio
        </button>
      </div>

      <div className="rounded-[24px] border border-primary/12 bg-white p-5">
        <p className="font-semibold text-primary">¡Ya casi, polénico! Revise y confirme su información.</p>
        <p className="mt-2 text-sm leading-6 text-mutedForeground">
          Al finalizar, su cuenta quedará activa y podrá participar de inmediato en la red.
        </p>

        <label className="mt-5 flex items-start gap-3">
          <input
            type="checkbox"
            checked={personal.acepta_politica_datos}
            onChange={(event) =>
              setPersonal((current) => ({ ...current, acepta_politica_datos: event.target.checked }))
            }
            className="mt-1 h-5 w-5 shrink-0 rounded border-primary text-primary"
          />
          <span className="text-sm leading-6 text-textMain">
            Acepto la{" "}
            <a href="https://forjandofuturos.org/politica-de-privacidad/" target="_blank" rel="noreferrer" className="font-semibold text-primary underline underline-offset-2">
              política de tratamiento de datos personales
            </a>
            .
          </span>
        </label>
        {stepHasAttempt && confirmationErrors.acepta_politica_datos ? (
          <p className="mt-3 text-xs font-medium text-[#8A3D22]">{confirmationErrors.acepta_politica_datos}</p>
        ) : null}

        <label className="mt-5 flex items-start gap-3">
          <input
            type="checkbox"
            checked={finalAcknowledged}
            onChange={(event) => setFinalAcknowledged(event.target.checked)}
            className="mt-1 h-5 w-5 shrink-0 rounded border-primary text-primary"
          />
          <span className="text-sm leading-6 text-textMain">
            Confirmo que la información suministrada es correcta y autorizo su publicación en la plataforma.
          </span>
        </label>

        {stepHasAttempt && confirmationErrors.finalAcknowledged ? (
          <p className="mt-3 text-xs font-medium text-[#8A3D22]">{confirmationErrors.finalAcknowledged}</p>
        ) : null}
      </div>
    </div>
  );
}
